import { useEffect, useRef, useState } from "react";
import { runsOnDevice } from "../api/client.js";

/**
 * Reveal a long grid a batch at a time.
 *
 * A page of 300 cards is one long task: on the 1 GB projector it blocked the
 * page for ~2.8 s, so Movies took six seconds to show anything (2026-09-20).
 * The first batch is what fills the screen; the rest arrive over the following
 * frames, which keeps the page responsive while the same 300 end up there. The
 * website renders the lot at once, as it always did.
 */
const FIRST = 60;
const STEP = 60;

export function useProgressiveList<T>(items: readonly T[]): T[] {
  const device = runsOnDevice();
  const [count, setCount] = useState(device ? FIRST : items.length);
  // A new list (genre, search, sort) starts again from the first batch.
  const listRef = useRef(items);
  if (listRef.current !== items) {
    listRef.current = items;
    if (device && count !== FIRST) setCount(FIRST);
  }

  useEffect(() => {
    if (!device) {
      if (count !== items.length) setCount(items.length);
      return;
    }
    if (count >= items.length) return;
    // A frame apart, so a press in between is still handled promptly.
    const timer = window.setTimeout(() => setCount((shown) => Math.min(shown + STEP, items.length)), 32);
    return () => window.clearTimeout(timer);
  }, [device, count, items.length]);

  return device ? items.slice(0, count) : (items as T[]);
}
